import { Permission } from '@/api/models/Permission';
import { useHasPermissions } from '@/hooks/use-has-permissions';
import { useSimulationErrorNotification } from '@/hooks/use-simulation-error-notification';

import { useSavePricelistSettings } from '@/pages/price-planning/simulation/simulation-queries';
import { connectColors, themeColors } from '@/theme/colors';
import { normalizeNumericString } from '@/utils/number';
import { Flex, Grid, Text } from '@lego/klik-ui';
import { useCallback, useMemo } from 'react';
import type {
  SimulationIndexRow,
  SimulationIndexRowForDisplay,
} from '../../utils/simulation-index-types';
import type { PricelistSettings } from '../../utils/simulation-types';
import IndexSectionCell from './index-section-cell';
import { formatIndexRowForDisplay } from './simulation-index-helpers';
import { DEFAULT_PAGE_MAX_WIDTH } from '@/components/page-wrapper/page-layout';

interface SimulationIndexSectionProps {
  ref: React.Ref<HTMLDivElement>;
  formattedInitialData: SimulationIndexRow[];
  calculatedData: SimulationIndexRow[];
  liveComputedIndexes?: SimulationIndexRow[];
  pricelistSettings: Map<string, PricelistSettings>;
  simulationId: number;
  isReadOnly: boolean;
  setPricelistSettings: (newSettings: Map<string, PricelistSettings>) => void;
}

const SimulationIndexSection = ({
  ref,
  formattedInitialData,
  calculatedData,
  liveComputedIndexes,
  pricelistSettings,
  simulationId,
  isReadOnly,
  setPricelistSettings,
}: SimulationIndexSectionProps) => {
  const canEditSimulationSettings = useHasPermissions([
    Permission.EditSimulationSettings,
  ]);
  const columnLabels: Record<keyof SimulationIndexRowForDisplay, string> = {
    priceList: 'Price List',
    currency: 'Currency',
    rrpIndex: 'RRP Index',
    rrpLocalLeadExclVatIndex: 'RRP LL Excl VAT Index',
    rm: 'RM%',
    lpLocalLeadIndex: 'LL LP Index',
    nipLocalLeadIndex: 'LL NIP Index',
    nipGlobalLeadIndex: 'GL NIP Index',
    listPriceFmcFactorTarget: 'LP/FMC Factor Target',
    listPriceFmcFactor: 'LP/FMC Factor',
    listPriceFmcFactorDifference: 'LP/FMC Factor Δ',
    salesRevenue: 'Sales Revenue',
    productCount: 'Product Count',
  };

  const displayData = useMemo(
    () => calculatedData.map(formatIndexRowForDisplay),
    [calculatedData],
  );
  const displayInitialData = useMemo(
    () => formattedInitialData.map(formatIndexRowForDisplay),
    [formattedInitialData],
  );
  const displayLiveData = useMemo(
    () => liveComputedIndexes?.map(formatIndexRowForDisplay),
    [liveComputedIndexes],
  );

  const showSimulationError = useSimulationErrorNotification();
  const { mutate: savePricelistSettings } = useSavePricelistSettings(
    simulationId,
    (error) => {
      showSimulationError(error, 'save price settings');
    },
  );

  const handleCellEdit = useCallback(
    (
      cellValue: string,
      columnKey: keyof SimulationIndexRowForDisplay,
      priceListId: number,
    ) => {
      if (!canEditSimulationSettings) return;

      const existingRow = pricelistSettings.get(priceListId.toString());
      if (!existingRow) return;
      const newValue = Number(normalizeNumericString(cellValue));
      const newMap = new Map(pricelistSettings);
      newMap.set(priceListId.toString(), {
        ...existingRow,
        [columnKey]: newValue,
      });
      setPricelistSettings(newMap);
      if (columnKey in existingRow) {
        savePricelistSettings({
          priceSettings: [{ priceUnitId: priceListId, [columnKey]: newValue }],
        });
      }
    },
    [
      canEditSimulationSettings,
      pricelistSettings,
      setPricelistSettings,
      savePricelistSettings,
    ],
  );

  return (
    <div style={{ width: '100%', background: connectColors.core.blue[50] }}>
      <div
        ref={ref}
        style={{
          maxWidth: DEFAULT_PAGE_MAX_WIDTH,
          marginBottom: '16px',
          paddingLeft: '16px',
          paddingRight: '16px',
          margin: '0 auto',
        }}
      >
        <Grid
          templateColumns={
            'minmax(0,3fr) minmax(0,2fr) minmax(0,3fr) minmax(0,2fr) minmax(0,2fr) minmax(0,2fr) minmax(0,2fr) minmax(0,3fr) minmax(0,3fr) minmax(0,2fr)  minmax(0,3fr) minmax(0,2fr)'
          }
          columnGap={'clamp(4px, 1vw, 24px)'}
          rowGap={'8px'}
          padding={'24px 0'}
        >
          {Object.keys(columnLabels).map((key) =>
            key === 'currency' ? null : (
              <div
                key={key}
                style={{
                  padding: '0',
                }}
              >
                {key === 'priceList' ? (
                  <Flex justify={'space-between'}>
                    <Text
                      className="label-xs-regular"
                      isTruncated
                      color={themeColors.content.subtle}
                    >
                      {columnLabels.priceList}
                    </Text>
                    <Text
                      className="label-xs-regular"
                      isTruncated
                      color={themeColors.content.subtle}
                    >
                      {columnLabels.currency}
                    </Text>
                  </Flex>
                ) : (
                  <Text
                    className="label-xs-regular"
                    isTruncated
                    color={themeColors.content.subtle}
                  >
                    {columnLabels[key as keyof typeof columnLabels]}
                  </Text>
                )}
              </div>
            ),
          )}
          {displayData.map((row) =>
            Object.keys(columnLabels).map((key) =>
              key === 'currency' ? null : (
                <IndexSectionCell
                  key={`${row.priceList.id}-${key}`}
                  row={row}
                  columnKey={key as keyof SimulationIndexRowForDisplay}
                  formattedInitialData={displayInitialData}
                  previewBaseData={displayLiveData}
                  isEditable={
                    key === 'listPriceFmcFactorTarget' &&
                    !!canEditSimulationSettings &&
                    !isReadOnly
                  }
                  handleCellEdit={handleCellEdit}
                />
              ),
            ),
          )}
        </Grid>
      </div>
    </div>
  );
};
export default SimulationIndexSection;
